/**
 * Client-side account session (pre-game only, never on the game path).
 *
 * The token comes from POST /login (or /create-account → login) and is kept
 * in localStorage under ONE key. It travels in Hello.auth; the server
 * resolves it and the account's username OVERRIDES whatever name the client
 * sends — so nothing stored here is authoritative, it is a credential plus
 * a cosmetic hint (the display name before the Welcome arrives).
 *
 * `ERR_AUTH_EXPIRED` is the server's word for a dead token — the session ended
 * (server restart, sign-out, the db reset). It is TERMINAL: the retry would
 * re-send the same dead credential, so net.ts drops it and stops reconnecting
 * rather than bouncing forever. Clear it here and show the login — but if the
 * login is already up, notice it, don't rebuild it (authScreen.showNotice).
 */
const TOKEN_KEY = "pd-auth-token";
const USERNAME_KEY = "pd-auth-username";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null, username?: string): void {
  if (token === null) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
  } else {
    localStorage.setItem(TOKEN_KEY, token);
    if (username !== undefined) localStorage.setItem(USERNAME_KEY, username);
  }
}

/** Cosmetic only — the server's Welcome is the truth for display. */
export function getAuthUsername(): string | null {
  return localStorage.getItem(USERNAME_KEY);
}

export interface AuthResult {
  ok: boolean;
  token?: string;
  username?: string;
  error?: string;
}

async function post(path: string, body: object): Promise<AuthResult> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, error: "server unreachable" };
  }
  let json: Record<string, unknown>;
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    return { ok: false, error: "bad response from server" };
  }
  if (json.ok === true) {
    return {
      ok: true,
      token: typeof json.token === "string" ? json.token : undefined,
      username: typeof json.username === "string" ? json.username : undefined,
    };
  }
  return { ok: false, error: typeof json.error === "string" ? json.error : `error ${res.status}` };
}

export const apiLogin = (username: string, password: string): Promise<AuthResult> =>
  post("/login", { username, password });

export interface CreateAccountInput {
  username: string;
  password: string;
  class: string;
  /** Saved look (outfit + region colours) — shape-checked server-side. */
  appearance: { outfit: Record<string, string | null>; colours: Record<string, number> };
}

export const apiCreateAccount = (input: CreateAccountInput): Promise<AuthResult> =>
  post("/create-account", input);

/** Sign out: release the account's session so a re-login is accepted.
 *  Fire-and-forget at the call site — the server also purges stale rows at
 *  its own startup, so a dropped POST only costs one later re-login. */
export const apiLogout = (token: string): Promise<AuthResult> => post("/logout", { token });
